import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useModalHistory } from '../../../utils/useModalHistory';
import type { Booking, Category, Machine, MachineOption, MachineUpdate, PanelState, Supplier } from '../types';
import type { Settings } from '../../settings/types';
import MachineDetailPanel from './MachineDetailPanel';
import BookingDetailPanel from './BookingDetailPanel';

interface SidePanelProps {
  panelState: PanelState;
  machines: Machine[];
  machinesReadOnly: boolean;
  bookingsReadOnly: boolean;
  projectName?: string;
  className?: string;
  suppliers: Supplier[];
  categories: Category[];
  options: MachineOption[];
  orgSettings: Pick<
    Settings,
    'order_email_subject' | 'order_email_body' | 'return_email_subject' | 'return_email_body'
  >;
  pickupHandinEnabled: boolean;
  onClose: () => void;
  onBackToMachine: (machineId: string) => void;
  onSelectBooking: (bookingId: string, machineId: string) => void;
  onUpdateMachine: (
    id: string,
    updates: MachineUpdate,
    related?: { supplier?: Machine['supplier'] }
  ) => Promise<void>;
  onDeleteMachine: (id: string) => Promise<void>;
  onDeleteBooking: (id: string) => Promise<void>;
  onAddBooking: () => void;
  onEditBooking: (bk: Booking) => void;
  onAddSupplier: (name: string, email: string) => Promise<Supplier | null>;
  onAddCategory: (name: string) => Promise<Category | null>;
  onAddOption: (name: string) => Promise<MachineOption | null>;
  onConfirmPickup: (bk: Booking) => void;
  onConfirmHandin: (bk: Booking) => void;
}

// The single side panel for both machines and bookings — never two floating layers.
// Which view renders is driven entirely by `panelState`; this component only owns the
// shared aside chrome (width/border/shadow, mobile back-button handling).
export default function SidePanel({
  panelState,
  machines,
  machinesReadOnly,
  bookingsReadOnly,
  projectName,
  className,
  suppliers,
  categories,
  options,
  orgSettings,
  pickupHandinEnabled,
  onClose,
  onBackToMachine,
  onSelectBooking,
  onUpdateMachine,
  onDeleteMachine,
  onDeleteBooking,
  onAddBooking,
  onEditBooking,
  onAddSupplier,
  onAddCategory,
  onAddOption,
  onConfirmPickup,
  onConfirmHandin,
}: SidePanelProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useModalHistory(onClose, isMobile && !!panelState);

  if (!panelState) return null;

  const machine = machines.find((m) => m.id === panelState.machineId) ?? null;
  if (!machine) return null;

  const booking = panelState.type === 'booking'
    ? machine.bookings?.find((b) => b.id === panelState.bookingId) ?? null
    : null;

  return (
    <aside
      className={
        className ??
        'flex flex-col w-96 border-l border-border bg-surface overflow-hidden z-20 shadow-[-6px_0_32px_rgba(0,0,0,0.22)] dark:shadow-[-6px_0_32px_rgba(0,0,0,0.55)]'
      }
      data-testid="detail-panel"
    >
      {panelState.type === 'machine' && (
        <MachineDetailPanel
          machine={machine}
          machinesReadOnly={machinesReadOnly}
          bookingsReadOnly={bookingsReadOnly}
          projectName={projectName}
          suppliers={suppliers}
          categories={categories}
          options={options}
          orgSettings={orgSettings}
          isMobile={isMobile}
          onClose={onClose}
          onUpdateMachine={onUpdateMachine}
          onDeleteMachine={onDeleteMachine}
          onAddBooking={onAddBooking}
          onSelectBooking={onSelectBooking}
          onAddSupplier={onAddSupplier}
          onAddCategory={onAddCategory}
          onAddOption={onAddOption}
          pickupHandinEnabled={pickupHandinEnabled}
        />
      )}
      {panelState.type === 'booking' && booking && (
        <BookingDetailPanel
          booking={booking}
          machine={machine}
          isReadOnly={bookingsReadOnly}
          pickupHandinEnabled={pickupHandinEnabled}
          onBack={() => onBackToMachine(machine.id)}
          onClose={onClose}
          onEdit={onEditBooking}
          onDelete={onDeleteBooking}
          onConfirmPickup={onConfirmPickup}
          onConfirmHandin={onConfirmHandin}
        />
      )}
    </aside>
  );
}
